import { faker } from '@faker-js/faker';
import { http, HttpResponse } from 'msw';

import { ONLINE_ENDPOINTS } from '@/config/api';
import { env } from '@/config/env';
import { axios } from '@/lib/axios';
import { networkDelay } from '@/test/mocks/utils';

import type { Logbook, LogbookType, LogbooksResponse } from '../types';

// Stable module IDs for consistent mock data across requests
// These should match STABLE_MODULE_IDS from assessments if used together
export const STABLE_MODULE_IDS = {
  ONGOING: 'a1b2c3d4-e5f6-4a5b-8c9d-0e1f2a3b4c5d',
  COMPLETED: 'f6e5d4c3-b2a1-4d5c-9e8f-7a6b5c4d3e2f',
} as const;

// Utility functions for generating consistent mock data
const generateLogbookId = (): string => `lb-${faker.string.alphanumeric(6)}`;
const generateAttemptId = (): string => faker.string.uuid();

const generateLogbookName = (): string =>
  faker.helpers.arrayElement([
    'Addition Practice Workbook',
    'Subtraction Mastery Logbook',
    'Multiplication Practice Journal',
    'Division Skills Workbook',
    'Reading Comprehension Logbook',
    'Grammar Practice Workbook',
    'Vocabulary Building Journal',
    'Writing Skills Logbook',
  ]);

const generateModuleName = (): string =>
  faker.helpers.arrayElement([
    'Numbers and Operations',
    'Reading Fundamentals',
    'Writing Basics',
    'Grammar Essentials',
    'Vocabulary Development',
  ]);

const generateTopicName = (): string =>
  faker.helpers.arrayElement([
    'Two Digit Addition',
    'Three Digit Subtraction',
    'Basic Multiplication',
    'Simple Division',
    'Reading Comprehension',
    'Sentence Structure',
    'Parts of Speech',
    'Word Meanings',
  ]);

const generateBatchName = (): string =>
  faker.helpers.arrayElement([
    'Batch Alpha',
    'Batch Beta',
    'Batch Gamma',
    'Batch Delta',
    'Batch Omega',
  ]);

// Format date to "DD MMM YYYY" format (date only, no time)
const formatLogbookDate = (date: Date): string => {
  const day = date.getDate().toString().padStart(2, '0');
  const month = date.toLocaleString('en-US', { month: 'short' });
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

// Generator for Logbook objects
const generateLogbook = (
  statusOverride?: 'LAUNCHED' | 'IN_PROGRESS' | 'SUBMITTED' | 'EVALUATED'
): Logbook => {
  const attemptStatus =
    statusOverride ??
    faker.helpers.arrayElement<
      'LAUNCHED' | 'IN_PROGRESS' | 'SUBMITTED' | 'EVALUATED'
    >(['LAUNCHED', 'IN_PROGRESS', 'SUBMITTED', 'EVALUATED']);

  const maxMarks = faker.number.int({ min: 10, max: 100 });
  const createdDate = faker.date.recent({ days: 30 });
  const created_at = formatLogbookDate(createdDate);

  // Determine is_redo based on status
  // For IN_PROGRESS, is_redo determines if it's continue-editing (false) or re-attempt (true)
  // Default to false, will be overridden in generateLogbooksMockData for specific cases
  const is_redo = false;

  // Determine marks, dates based on status
  let marks: string | null = null;
  let started_at: string | null = null;
  let submitted_at: string | null = null;
  let evaluated_at: string | null = null;

  switch (attemptStatus) {
    case 'SUBMITTED': {
      // Submitted: no marks yet, has started_at and submitted_at
      const startedDate = faker.date.between({
        from: createdDate,
        to: new Date(),
      });
      const submittedDate = faker.date.between({
        from: startedDate,
        to: new Date(),
      });
      started_at = formatLogbookDate(startedDate);
      submitted_at = formatLogbookDate(submittedDate);
      break;
    }
    case 'EVALUATED': {
      // Evaluated: has marks, has all dates
      marks = faker.number.int({ min: 0, max: maxMarks }).toString();
      const startedDate = faker.date.between({
        from: createdDate,
        to: new Date(),
      });
      const submittedDate = faker.date.between({
        from: startedDate,
        to: new Date(),
      });
      const evaluatedDate = faker.date.between({
        from: submittedDate,
        to: new Date(),
      });
      started_at = formatLogbookDate(startedDate);
      submitted_at = formatLogbookDate(submittedDate);
      evaluated_at = formatLogbookDate(evaluatedDate);
      break;
    }
    case 'IN_PROGRESS': {
      // In progress: has started_at
      const startedDate = faker.date.between({
        from: createdDate,
        to: new Date(),
      });
      started_at = formatLogbookDate(startedDate);
      break;
    }
    case 'LAUNCHED': {
      // Launched: no started_at, no submitted_at, no evaluated_at
      // (already initialized to null, no action needed)
      break;
    }
  }

  return {
    attempt_id: generateAttemptId(),
    logbook_id: generateLogbookId(),
    logbook_name: generateLogbookName(),
    logbook_description: faker.datatype.boolean({ probability: 0.3 })
      ? faker.lorem.sentence()
      : null,
    module_id: faker.string.uuid(),
    module_name: generateModuleName(),
    topic_id: faker.datatype.boolean({ probability: 0.8 })
      ? faker.string.uuid()
      : null,
    topic_name: faker.datatype.boolean({ probability: 0.8 })
      ? generateTopicName()
      : null,
    logbook_type: faker.helpers.arrayElement<LogbookType>([
      'TOPIC_LEVEL',
      'MODULE_LEVEL',
    ]),
    batch_id: faker.string.uuid(),
    batch_name: generateBatchName(),
    doc_link: faker.internet.url(),
    attempt_status: attemptStatus,
    is_redo,
    created_at,
    started_at,
    submitted_at,
    evaluated_at,
    marks,
    maximum_marks: maxMarks.toString(),
    redo_count: faker.number.int({ min: 0, max: 3 }),
    feedback: faker.datatype.boolean({ probability: 0.2 })
      ? faker.lorem.paragraph()
      : null,
  };
};

// Main mock data generator
export const generateLogbooksMockData = (): LogbooksResponse => {
  // Generate a mix of all statuses to showcase all variants
  const logbooks: Logbook[] = [];

  // 1. LAUNCHED → newly-launched
  const launchedLogbook = generateLogbook('LAUNCHED');
  logbooks.push(launchedLogbook);

  // 2. IN_PROGRESS & is_redo = false → continue-editing
  const continueEditingLogbook = generateLogbook('IN_PROGRESS');
  // Create a new object with is_redo = false
  logbooks.push({
    ...continueEditingLogbook,
    is_redo: false,
  });

  // 3. IN_PROGRESS & is_redo = true → re-attempt
  const reAttemptLogbook = generateLogbook('IN_PROGRESS');
  // For re-attempt, we need to add submitted_at from previous attempt
  const reAttemptCreatedDate = faker.date.recent({ days: 30 });
  const reAttemptStartedDate = faker.date.between({
    from: reAttemptCreatedDate,
    to: new Date(),
  });
  const reAttemptSubmittedDate = faker.date.between({
    from: reAttemptStartedDate,
    to: new Date(),
  });
  logbooks.push({
    ...reAttemptLogbook,
    is_redo: true,
    submitted_at: formatLogbookDate(reAttemptSubmittedDate), // Previous submission date
    marks: faker.number.int({ min: 0, max: 49 }).toString(), // Previous failing score
  });

  // 4. SUBMITTED → handed-in (marks not updated)
  const submittedLogbook = generateLogbook('SUBMITTED');
  logbooks.push(submittedLogbook);

  // 5. EVALUATED → marks-updated (marks updated)
  const evaluatedLogbook = generateLogbook('EVALUATED');
  logbooks.push(evaluatedLogbook);

  // Add a few more random logbooks to reach 7-8 total
  const numLogbooks = faker.number.int({ min: 7, max: 8 });
  const remaining = numLogbooks - logbooks.length;
  for (let i = 0; i < remaining; i++) {
    logbooks.push(generateLogbook());
  }

  // Shuffle to randomize order
  const shuffled = faker.helpers.shuffle(logbooks);

  return {
    status: 'Success',
    message: 'Logbooks retrieved successfully',
    logbooks: shuffled,
  };
};

// Mock handler
export const getLogbooksMockHandler = http.get(
  `${env.API_URL}${ONLINE_ENDPOINTS.USER.LOGBOOKS}`,
  async (): Promise<HttpResponse<LogbooksResponse>> => {
    await networkDelay();

    // Generate logbooks for stable module IDs to ensure they match modules in the list
    const allLogbooks: Logbook[] = [];

    // Generate multiple sets of logbooks for ONGOING module to ensure visibility
    for (let i = 0; i < 2; i++) {
      const ongoingLogbooks = generateLogbooksMockData().logbooks.map(
        (logbook): Logbook => ({
          ...logbook,
          module_id: STABLE_MODULE_IDS.ONGOING,
        })
      );
      allLogbooks.push(...ongoingLogbooks);
    }

    // Generate multiple sets of logbooks for COMPLETED module to ensure visibility
    for (let i = 0; i < 2; i++) {
      const completedLogbooks = generateLogbooksMockData().logbooks.map(
        (logbook): Logbook => ({
          ...logbook,
          module_id: STABLE_MODULE_IDS.COMPLETED,
        })
      );
      allLogbooks.push(...completedLogbooks);
    }

    // Shuffle to randomize order
    const shuffled = faker.helpers.shuffle(allLogbooks);

    return HttpResponse.json({
      status: 'Success',
      message: 'Logbooks retrieved successfully',
      logbooks: shuffled,
    });
  }
);

// API function
export const getLogbooks = (): Promise<LogbooksResponse> => {
  return axios.get(ONLINE_ENDPOINTS.USER.LOGBOOKS);
};
